import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { LoaderFunction, RouteMatch, RouteObject, matchRoutes } from 'react-router-dom';
import type { NavigateOptions } from 'react-router/dist/lib/context';
import { WidgetBase } from '@/core/widgets/model/news-widget';
import { WidgetHostContext } from '@/core/widgets/contexts/widget-host-context';
import { Loading, ErrorBoundary, errorBoundaryReloadMessage } from '@/shared/components/export';

export interface WidgetNavigateFunction {
  (to: string, options?: NavigateOptions): void;
  (delta: number): void;
}
export interface LocalWidgetRouterState {
  path: string;
  routes: RouteObject[];
  route?: RouteObject;
  navigate: WidgetNavigateFunction;
  useLocal: boolean;
  loaderData: unknown;
  params: any;
  basePath: string;
  loading: boolean;
}

export const LocalWidgetRouterContext = React.createContext<LocalWidgetRouterState>(
  {} as LocalWidgetRouterState,
);

export interface WidgetRouteState {
  outlet: React.ReactNode;
  loaderData: unknown;
}

export const WidgetRouteContext = React.createContext<WidgetRouteState | null>(null);

interface ResolvedMatch {
  match: RouteMatch;
  key: string;
  loaded: boolean;
  data: unknown;
}

interface ResolvedRoute {
  path: string;
  route?: RouteObject;
  params: any;
  loaderData: unknown;
  matches: ResolvedMatch[];
  pendingFrom?: number;
  error?: unknown;
}

function resolveMatches(
  routes: RouteObject[],
  pathname: string,
  previous: ResolvedMatch[],
): ResolvedMatch[] {
  const matches = matchRoutes(routes, pathname) ?? [];
  let siblings = routes;
  let position = '';

  return matches.map((match, depth) => {
    position += `/${siblings.indexOf(match.route)}`;
    siblings = match.route.children ?? [];

    const key = `${position}@${match.pathname}`;
    const isLeaf = depth === matches.length - 1;
    const reusable = previous[depth];
    if (!isLeaf && reusable?.loaded && reusable.key === key) return { ...reusable, match };

    return { match, key, loaded: !match.route.loader, data: undefined };
  });
}

function RethrowError({ error }: { error: unknown }): null {
  throw error;
}

function renderMatches(matches: ResolvedMatch[], pendingFrom?: number): React.ReactNode {
  return matches.reduceRight<React.ReactNode>((outlet, { match, data }, depth) => {
    if (pendingFrom !== undefined && depth >= pendingFrom) {
      return depth === pendingFrom ? <Loading /> : null;
    }
    return (
      <WidgetRouteContext.Provider value={{ outlet, loaderData: data }}>
        {match.route.element ?? outlet}
      </WidgetRouteContext.Provider>
    );
  }, null);
}

export default function LocalWidgetRouter({
  widget,
  onNavigate,
  path,
}: React.PropsWithChildren<{
  widget: WidgetBase;
  onNavigate: (s: string) => void;
  path?: string;
}>) {
  const widgetHost = useContext(WidgetHostContext);

  const [resolved, setResolved] = useState<ResolvedRoute>({
    path: path || widget.defaultRoute,
    route: undefined,
    params: undefined,
    loaderData: undefined,
    matches: [],
    pendingFrom: 0,
  });
  const [history, setHistory] = useState<string[]>([]);

  const resolvedRef = useRef(resolved);
  resolvedRef.current = resolved;
  const historyRef = useRef(history);
  historyRef.current = history;

  const navigationIdRef = useRef(0);

  const navigateToPath = useCallback(
    async (p: string) => {
      const navigationId = ++navigationIdRef.current;
      const [pathBase] = p.split('?');
      let matches = resolveMatches(
        widget.routes,
        `/${pathBase.replace(/^\/+/, '')}`,
        resolvedRef.current.matches,
      );
      const pending = matches.flatMap((m, depth) => (m.loaded ? [] : [depth]));

      if (pending.length) {
        const pendingMatches = matches;
        setResolved(prev => ({
          ...prev,
          matches: pendingMatches,
          pendingFrom: pending[0],
          error: undefined,
        }));

        try {
          const results = await Promise.all(
            pending.map(depth =>
              (pendingMatches[depth].match.route.loader as LoaderFunction)({
                params: pendingMatches[depth].match.params,
              } as any),
            ),
          );
          matches = pendingMatches.map((m, depth) =>
            pending.includes(depth)
              ? { ...m, loaded: true, data: results[pending.indexOf(depth)] }
              : m,
          );
        } catch (error) {
          if (navigationId === navigationIdRef.current) {
            setResolved(prev => ({ ...prev, pendingFrom: undefined, error }));
          }
          return;
        }
      }

      if (navigationId !== navigationIdRef.current) return;

      const leaf = matches[matches.length - 1];
      setResolved({
        path: p,
        route: leaf?.match.route,
        params: leaf?.match.params,
        loaderData: leaf?.data,
        matches,
      });
    },

    [widget],
  );

  const navigate: WidgetNavigateFunction = useCallback(
    (to: string | number, _options?: NavigateOptions) => {
      (async () => {
        if (typeof to === 'string') {
          setHistory(prev => [...prev, resolvedRef.current.path]);
          await navigateToPath(to);
        } else {
          const target = historyRef.current[historyRef.current.length - 1];
          setHistory(prev => prev.slice(0, -1));
          await navigateToPath(target ?? widget.defaultRoute);
        }
      })();
    },

    [navigateToPath, widget.defaultRoute],
  );

  useEffect(() => {
    navigateToPath(path || widget.defaultRoute);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!path || path === resolvedRef.current.path) return;
    navigateToPath(path);
    // eslint-disable-next-line
  }, [path]);

  useEffect(() => {
    onNavigate(resolved.path);
    // eslint-disable-next-line
  }, [resolved.path]);

  useEffect(() => {
    return widgetHost.registerWidgetNavigator(widget.id, navigateToPath);
  }, [widgetHost, widget.id, navigateToPath]);

  const state: LocalWidgetRouterState = {
    path: resolved.path,
    routes: widget.routes,
    route: resolved.route,
    navigate,
    useLocal: true,
    loaderData: resolved.loaderData,
    params: resolved.params,
    basePath: widget.id,
    loading: resolved.pendingFrom !== undefined,
  };

  return (
    <LocalWidgetRouterContext.Provider value={state}>
      <ErrorBoundary
        errorMessage={errorBoundaryReloadMessage(() => {
          navigate(widget.defaultRoute);
        })}
      >
        {resolved.error !== undefined ? (
          <RethrowError error={resolved.error} />
        ) : resolved.matches.length ? (
          renderMatches(resolved.matches, resolved.pendingFrom)
        ) : state.loading ? (
          <Loading />
        ) : (
          <div>
            Path not found: {state.path} {/* eslint-disable-next-line */}
            <a
              onClick={event => {
                event.preventDefault();
                event.stopPropagation();
                navigate(-1);
              }}
              href={''}
            >
              Back
            </a>
          </div>
        )}
      </ErrorBoundary>
    </LocalWidgetRouterContext.Provider>
  );
}
